<template>
	<div>
		<v-data-table
			dense
			:loading="loading"
			:no-data-text="$t('no-data-available')"
			:loading-text="$t('loading')"
			:headers="facilityHeaders"
			:items="groupedInvoices"
			item-key="clientName"
			single-expand
			:items-per-page="-1"
			hide-default-header
			:height="pageHeight(174)"
			ref="table"
			:footer-props="getDataTableFooterProps('InvoicesTable')"
			fixed-header>
			<template #item="{ item, index, expand, isExpanded }">
				<tr :class="[getRowsForDataTable(index), { 'expanded-row': isExpanded }]" @click="expand(!isExpanded)">
					<td class="table-border-bottom text-left d-flex align-center">
						<v-btn small icon>
							<v-icon small color="primary">
								{{ isExpanded ? 'mdi-arrow-down-drop-circle' : 'mdi-arrow-right-drop-circle' }}
							</v-icon>
						</v-btn>
						{{ item.clientName }}
						<v-chip class="ml-2 white--text" pill x-small color="primary">
							{{ filteredInvoices(item).length }}
						</v-chip>
					</td>
				</tr>
			</template>
			<template #expanded-item="{ item }">
				<tr style="cursor: default; background-color: #fff !important">
					<td class="pa-3">
						<v-data-table
							:key="item.id"
							:items="filteredInvoices(item)"
							:headers="invoiceHeaders"
							dense
							:items-per-page="-1"
							hide-default-footer
							class="data-table-outline"
							:height="pageHeight(83)"
							fixed-header>
							<template #item="{ item, index }">
								<tr :class="getRowsForDataTable(index)">
									<td class="text-left pl-4 font-size13 font-weight-medium">
										{{ item.invoiceNumber }}
									</td>
									<td class="text-left pl-4 font-size13 font-weight-medium">
										{{ getMonthName(item.invoiceMonth) }}
									</td>
									<td class="text-left pl-4 font-size13 font-weight-medium">
										{{ item.invoiceYear }}
									</td>
									<td class="text-left font-size13 font-weight-medium">
										{{ item.locationName || '-' }}
									</td>
									<td class="text-left font-size13 font-weight-medium">
										{{ formatCurrency(item.grandTotal) }}
									</td>
									<td class="text-center">
										<v-select
											class="thin-select"
											style="max-width: 300px"
											outlined
											dense
											hide-details
											return-object
											:items="getStatusOptions(item)"
											v-model="item.currentStatus"
											@change="changeStatus(item.id, item.currentStatus)"
											:menu-props="{ offsetY: true }" />
									</td>
									<td class="text-right">
										<v-tooltip color="primary" left>
											<template #activator="{ on, attrs }">
												<v-icon
													v-on="on"
													v-bind="attrs"
													v-if="roleName === 'SYSTEM_ADMIN'"
													@click="invoiceDetails(item)"
													class="mr-2 mdi mdi-pencil"
													color="primary" />
											</template>
											<span>{{ $t('edit') }}</span>
										</v-tooltip>
										<v-tooltip color="primary" left>
											<template #activator="{ on, attrs }">
												<v-icon
													v-on="on"
													v-bind="attrs"
													v-if="roleName === 'ORGANIZATION_ADMIN'"
													@click="generatePDF(item)"
													class="mr-2 mdi mdi-file-pdf-box"
													color="primary" />
											</template>
											<span>{{ $t('download-pdf') }}</span>
										</v-tooltip>
										<v-tooltip color="primary" left>
											<template #activator="{ on, attrs }">
												<v-icon
													v-on="on"
													v-bind="attrs"
													@click="openInNewTab(item)"
													class="mr-2 mdi mdi-open-in-new"
													color="primary" />
											</template>
											<span>{{ $t('open-in-new-tab') }}</span>
										</v-tooltip>
										<v-tooltip color="primary" left>
											<template #activator="{ on, attrs }">
												<v-icon
													v-on="on"
													v-bind="attrs"
													v-if="roleName === 'SYSTEM_ADMIN'"
													@click="openEmailDialog(item)"
													:disabled="['Paid'].includes(item.currentStatus.text)"
													class="mdi mdi-email-arrow-right"
													color="primary" />
											</template>
											<span>{{ $t('send-email') }}</span>
										</v-tooltip>
									</td>
								</tr>
							</template>
						</v-data-table>
					</td>
				</tr>
			</template>
		</v-data-table>
		<v-dialog persistent width="auto" v-if="emailDialog" v-model="emailDialog">
			<v-card style="min-height: 90vh; max-height: 90vh; width: 80vw; min-width: 400px" class="d-flex flex-column">
				<v-card-title class="card-header text-left fixed-card-title2">
					<v-row no-gutters>
						<v-col class="align-center d-flex" cols="10">
							<v-toolbar-title>
								{{ $t('select-emails') }} {{ $t('for') }} {{ selectedInvoice.invoiceNumber }}
							</v-toolbar-title>
						</v-col>
						<v-spacer />
						<v-icon @click="closeEmailDialog()" class="white-icon">mdi-close</v-icon>
					</v-row>
				</v-card-title>
				<v-card-text class="pa-4 pb-2 flex-grow-1 overflow-auto d-flex flex-column">
					<v-row dense>
						<v-col cols="5">
							<v-row dense class="flex-column" style="height: calc(100% + 8px); width: calc(100% + 8px)">
								<v-col class="flex-grow-0">
									<v-btn-toggle
										v-model="showingInvoiceOrBilling"
										row
										active-class="dark_purple white--text"
										mandatory
										style="width: 100%"
										class="white">
										<v-btn value="invoice" :input-value="showingInvoiceOrBilling" style="width: 50%; height: 36px">
											{{ $t('view-invoice') }}
										</v-btn>
										<v-btn value="billing" :input-value="showingInvoiceOrBilling" style="width: 50%; height: 36px">
											{{ $t('view-billing') }}
										</v-btn>
									</v-btn-toggle>
								</v-col>
								<v-col class="flex-grow-0">
									<div class="d-flex justify-space-between">
										<v-text-field
											dense
											outlined
											hide-details
											class="thin-text-field"
											:label="$t('custom-email')"
											v-model="customEmail"
											@keydown.enter="addCustomEmail" />
										<v-btn color="dark_purple ml-2" elevation="0" style="color: white" @click="addCustomEmail">
											{{ $t('add-email') }}
										</v-btn>
									</div>
								</v-col>
								<v-col class="flex-grow-0">
									<div class="d-flex justify-space-between align-center pl-2">
										{{ $t('email') }}
										<div class="d-flex">
											<span class="text-caption font-weight-bold" style="width: 32px">To</span>
											<span class="text-caption font-weight-bold" style="width: 32px">CC</span>
											<span class="text-caption font-weight-bold" style="width: 32px">None</span>
										</div>
									</div>
									<div style="max-height: 200px; overflow-y: auto" class="py-1">
										<div
											class="d-flex justify-space-between align-center pl-2"
											v-for="(email, idx) in customEmails"
											:key="email">
											<span>
												{{ email }}
												<v-icon color="primary" size="22" @click="removeCustomEmail(email, idx)">mdi-close</v-icon>
											</span>
											<v-radio-group v-model="emailSelections[email]" dense row hide-details class="mt-0 pt-0">
												<div class="d-flex align-center">
													<div class="d-flex justify-center" style="width: 32px">
														<v-radio value="to" class="no-radio-margin" :ripple="false" />
													</div>
													<div class="d-flex justify-center" style="width: 32px">
														<v-radio value="cc" class="no-radio-margin" :ripple="false" />
													</div>
													<div class="d-flex justify-center" style="width: 32px">
														<v-radio value="none" class="no-radio-margin" :ripple="false" />
													</div>
												</div>
											</v-radio-group>
										</div>
										<div
											class="d-flex justify-space-between align-center pl-2"
											v-for="email in contactEmails"
											:key="email">
											{{ email }}
											<v-radio-group v-model="emailSelections[email]" dense row hide-details class="mt-0 pt-0">
												<div class="d-flex align-center">
													<div class="d-flex justify-center" style="width: 32px">
														<v-radio value="to" class="no-radio-margin" :ripple="false" />
													</div>
													<div class="d-flex justify-center" style="width: 32px">
														<v-radio value="cc" class="no-radio-margin" :ripple="false" />
													</div>
													<div class="d-flex justify-center" style="width: 32px">
														<v-radio value="none" class="no-radio-margin" :ripple="false" />
													</div>
												</div>
											</v-radio-group>
										</div>
									</div>
								</v-col>
								<v-col class="flex-grow-0">
									<v-text-field
										dense
										hide-details
										outlined
										v-model="emailSubject"
										:label="$t('subject')"
										@input="isCustom = true" />
								</v-col>
								<v-col class="flex-grow-1 d-flex">
									<v-textarea
										dense
										hide-details
										outlined
										v-model="emailBody"
										no-resize
										class="full-height-textarea"
										:label="$t('body')"
										@input="isCustom = true" />
								</v-col>
								<v-col class="flex-grow-0">
									<div class="text-left">
										<v-checkbox v-model="includeInvoice" :label="pdfFileName" dense color="primary" hide-details />
									</div>
									<div class="text-left">
										<v-checkbox
											v-model="includedFiles"
											:label="`RPM_Billing_${fileNamePart}.csv\\xlsx`"
											value="RPM"
											dense
											color="primary"
											hide-details />
									</div>
									<div class="text-left">
										<v-checkbox
											v-model="includedFiles"
											:label="`RTM_Billing_${fileNamePart}.csv\\xlsx`"
											value="RTM"
											dense
											color="primary"
											hide-details />
									</div>
									<div class="text-left">
										<v-checkbox
											v-model="includedFiles"
											:label="`CCM_Billing_${fileNamePart}.csv\\xlsx`"
											value="CCM"
											dense
											color="primary"
											hide-details />
									</div>
								</v-col>
							</v-row>
						</v-col>
						<v-col cols="7" class="d-flex flex-column" style="overflow: hidden">
							<iframe
								v-if="showingInvoiceOrBilling === 'invoice' && invoicePdfURL"
								:src="`${invoicePdfURL}#toolbar=0`"
								width="100%"
								height="100%"
								style="border: none; border-bottom: #000 1px solid; flex-grow: 1" />
							<div
								class="flex-grow-1"
								v-else-if="tableHeaders.length > 0"
								style="position: relative; height: 0; min-height: 0">
								<v-data-table
									:items-per-page="-1"
									:items="tableItems"
									:headers="tableHeaders"
									class="medical-staff-data-table full-height-table"
									fixed-header
									dense>
									<template #[`item.Birthdate`]="{ item }"> {{ formatterFilter(item.Birthdate, 'date_med') }}</template>
									<template #[`item.UserCreatedAt`]="{ item }">
										{{ formatterFilter(item.UserCreatedAt, 'date_med') }}
									</template>
								</v-data-table>
							</div>
							<div v-else class="flex-grow-1 d-flex justify-center align-center">
								{{ $t('error-loading-data') }}
							</div>
						</v-col>
					</v-row>
				</v-card-text>
				<v-card-actions class="d-flex align-center justify-end px-4">
					<v-btn
						color="primary2"
						style="color: white; width: 100%"
						elevation="0"
						:disabled="isSendDisabled"
						@click="sendEmail()">
						{{ $t('send') }}
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
		<v-dialog v-if="newBodyDialog" v-model="newBodyDialog" persistent width="400px">
			<v-card style="max-height: 30vh; width: 100%" class="d-flex flex-column">
				<v-card-text class="pt-4">
					<div>{{ $t('would-you-like-to-use-email-template?') }}</div>
					<div>{{ $t('all-changes-will-be-lost') }}</div>
				</v-card-text>
				<v-card-actions>
					<v-row dense>
						<v-col cols="6">
							<v-btn outlined elevation="0" @click="newBodyDialog = false" block>
								{{ $t('no') }}
							</v-btn>
						</v-col>
						<v-col cols="6">
							<v-btn
								color="primary2"
								style="color: white"
								variant="elevated"
								elevation="0"
								@click="getEmailTemplate()"
								block>
								{{ $t('yes') }}
							</v-btn>
						</v-col>
					</v-row>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</div>
</template>
<script>
import { read, write, utils } from 'xlsx';
import { formatterFilter } from '@/utils/luxon-formater';
import { mapState, mapGetters } from 'vuex';
import { hospitalWhite } from '@/assets';
import {
	showSuccessAlert,
	showErrorAlert,
	getDataTableFooterProps,
	getRowsForDataTable,
	getMonthName,
} from '@/utils/utilities';

export default {
	data: () => ({
		dialog: false,
		dialogType: null,
		loading: false,
		togglers: {},
		search: '',
		headerClass: 'black_inner--text !important',
		expanded: [],
		hospital_white: hospitalWhite,
		selectedInvoice: null,
		headersTable: [
			{ text: 'Organization', value: 'organization' },
			{ text: 'Count', value: 'count' },
		],
		emailDialog: false,
		emailSelections: {},
		customEmails: [],
		customEmail: '',
		tableHeaders: [],
		tableItems: [],
		sendBilling: false,
		showingInvoiceOrBilling: 'invoice',
		invoicePdfURL: null,
		rpmBuffer: null,
		rtmBuffer: null,
		ccmBuffer: null,
		emailSubject: '',
		emailBody: '',
		pdfFileName: '',
		fileNamePart: '',
		includedFiles: [],
		includeInvoice: true,
		isCustom: false,
		newBodyDialog: false,
	}),
	computed: {
		...mapState({
			allInvoices: (state) => state.invoices.allInvoices,
			verifiersFromRepo: (state) => state.verifiers.verifiers,
			generatedPdfFile: (state) => state.invoices.generatedPdfFile,
			contactEmails: (state) => state.invoices.contactEmails,
			generatedRPMCsvFile: (state) => state.billings.generatedRPMCsvFile,
			generatedRTMCsvFile: (state) => state.billings.generatedRTMCsvFile,
			generatedCCMCsvFile: (state) => state.billings.generatedCCMCsvFile,
		}),
		...mapGetters({
			roleName: 'authentication/getRole',
			delay: 'authentication/getDelay',
			appBarSearch: 'filterbar/getSearch',
			pageHeight: 'pageHeight',
		}),
		facilityHeaders () {
			return [{ text: this.$t('facility-name'), value: 'clientName', align: 'start' }];
		},
		invoiceHeaders () {
			return [
				{
					text: this.$t('header-invoice-number'),
					value: 'invoiceNumber',
					class: this.headerClass,
					width: 200,
					id: 1,
				},
				{
					text: this.$t('invoice-month'),
					value: 'invoiceMonth',
					class: this.headerClass,
					id: 2,
				},
				{
					text: this.$t('invoice-year'),
					value: 'invoiceYear',
					class: this.headerClass,
					id: 3,
				},
				{
					text: this.$t('location-name'),
					value: 'locationName',
					class: this.headerClass,
					id: 4,
				},
				{
					text: this.$t('header-invoice-grand-total'),
					value: 'grandTotal',
					class: this.headerClass,
					id: 5,
				},
				{
					text: this.$t('status'),
					value: 'currentStatus',
					class: this.headerClass,
					id: 6,
				},
				{
					text: '',
					value: '',
					class: this.headerClass,
					sortable: false,
					id: 7,
				},
			];
		},
		verifiersInvoiceStatus () {
			if (this.verifiersFromRepo !== null) {
				const verifiersInvoiceStatus = this.verifiersFromRepo.filter((v) => v.rvDomain === 'INVOICE_STATUS');
				return verifiersInvoiceStatus;
			} else {
				return [];
			}
		},
		groupedInvoices () {
			return this.allInvoices.reduce((groups, invoice) => {
				let group = groups.find((g) => g.clientName === invoice.clientName);
				if (!group) {
					group = { clientName: invoice.clientName, invoices: [] };
					groups.push(group);
				}
				if (Object.keys(invoice.currentStatus).length === 1) {
					const status = Object.entries(invoice.currentStatus);
					invoice.currentStatus = { value: status[0][0], text: status[0][1] };
				}
				group.invoices.push(invoice);
				return groups;
			}, []);
		},
		isSendDisabled () {
			const selections = Object.values(this.emailSelections);
			return !selections.includes('to') || (!this.includeInvoice && this.includedFiles.length === 0);
		},
	},
	async mounted () {
		await this.getVerifiers();
		await this.getInvoices();
	},
	methods: {
		getMonthName,
		getRowsForDataTable,
		getDataTableFooterProps,
		formatterFilter,
		formattedStatuses (statusList) {
			return statusList.map((obj) => {
				const [key, value] = Object.entries(obj)[0];
				return { value: parseInt(key), text: value };
			});
		},
		getStatusOptions (invoice) {
			const nextSteps = this.formattedStatuses(invoice.nextStatuses);
			return [invoice.currentStatus, ...nextSteps];
		},
		async changeStatus (invoiceId, newStatus) {
			this.loading = true;
			await this.$store.dispatch('invoices/changeInvoiceStatus', { invoiceId, invoiceStatus: newStatus.value });
			this.loading = false;
		},
		async generatePDF (item) {
			const invoiceData = { invoiceId: item.id || -1 };
			await this.$store.dispatch('invoices/getInvoicePdf', invoiceData).then(() => {
				const regex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
				const match = this.generatedPdfFile.headers['content-disposition'].match(regex);
				showSuccessAlert(this.$t('pdf-downloaded'));
				const url = window.URL.createObjectURL(new Blob([this.generatedPdfFile.data]));
				const link = document.createElement('a');
				link.href = url;
				link.setAttribute('download', match ? match[1].replace(/['"]/g, '') : `invoice-${invoiceData.invoiceId}.pdf`);
				document.body.appendChild(link);
				link.click();
			});
			// showErrorAlert(this.$t('failed!'));
		},
		async openInNewTab (item) {
			const invoiceData = { invoiceId: item.id || -1 };
			await this.$store.dispatch('invoices/getInvoicePdf', invoiceData);
			const url = window.URL.createObjectURL(new Blob([this.generatedPdfFile.data], { type: 'application/pdf' }));
			window.open(url, '_blank');
		},
		async openEmailDialog (item) {
			this.loading = true;
			this.emailSelections = {};
			this.selectedInvoice = item;
			await Promise.all([
				this.$store.dispatch('invoices/getContactInvoicesEmails', item.id),
				this.getInvoicePdf(),
				this.generateRPMCsv(),
				this.generateRTMCsv(),
				this.generateCCMCsv(),
			]);
			const date = new Date(item.invoiceYear, item.invoiceMonth - 1);
			this.emailSubject = this.generateInvoiceOnlySubject();
			this.emailBody = this.generateInvoiceOnlyBody(
				item.grandTotal,
				formatterFilter(date, 'date_med_month'),
				formatterFilter(item.dueDate, 'date_med'),
			);
			this.contactEmails.forEach((email) => {
				this.emailSelections[email] = 'none';
			});
			this.loading = false;
			this.emailDialog = true;
		},
		closeEmailDialog () {
			this.emailDialog = false;
			this.emailSelections = {};
			this.includedFiles = [];
			this.customEmails = [];
			this.tableHeaders = [];
			this.tableItems = [];
			this.emailSubject = '';
			this.emailBody = '';
			this.includeInvoice = true;
		},
		async sendEmail () {
			this.loading = true;
			const fd = new FormData();
			if (this.includeInvoice) {
				const pdfBlob = new Blob([this.generatedPdfFile.data], { type: 'application/pdf' });
				fd.append('attachments', pdfBlob, this.pdfFileName);
			}
			fd.append('bodyEmail', `<pre>${this.emailBody || ''}</pre>`);
			if (this.includedFiles.length > 0) {
				const appendXlsx = (buffer, name) => {
					if (buffer) {
						const excelBinary = write(buffer, { bookType: 'xlsx', type: 'array' });
						const blob = new Blob([excelBinary], {
							type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
						});
						fd.append('attachments', blob, `${name}.xlsx`);
					}
				};
				if (this.includedFiles.includes('RPM') && this.generatedRPMCsvFile) {
					fd.append('attachments', this.generatedRPMCsvFile, `RPM_Billing_${this.fileNamePart}.csv`);
					appendXlsx(this.rpmBuffer, `RPM_Billing_${this.fileNamePart}`);
				}
				if (this.includedFiles.includes('RTM') && this.generatedRTMCsvFile) {
					fd.append('attachments', this.generatedRTMCsvFile, `RTM_Billing_${this.fileNamePart}.csv`);
					appendXlsx(this.rtmBuffer, `RTM_Billing_${this.fileNamePart}`);
				}
				if (this.includedFiles.includes('CCM') && this.generatedCCMCsvFile) {
					fd.append('attachments', this.generatedCCMCsvFile, `CCM_Billing_${this.fileNamePart}.csv`);
					appendXlsx(this.ccmBuffer, `CCM_Billing_${this.fileNamePart}`);
				}
			}
			const toList = Object.keys(this.emailSelections).filter((email) => this.emailSelections[email] === 'to');
			const ccList = Object.keys(this.emailSelections).filter((email) => this.emailSelections[email] === 'cc');
			const payload = {
				formData: fd,
				toEmails: toList.join(','),
				ccEmails: ccList.join(','),
				emailSubject: this.emailSubject,
			};
			const res = await this.$store.dispatch('invoices/sendMultipleAttachmentsByMail', payload);
			if (this.invoicePdfURL) {
				URL.revokeObjectURL(this.invoicePdfURL);
				this.invoicePdfURL = null;
			}
			this.closeEmailDialog();
			this.loading = false;
			if (res.invoiceSentSuccesfully && this.selectedInvoice.currentStatus.value === '0') {
				await this.changeStatus(this.selectedInvoice.id, { value: 1, title: 'Sent' })
					.then(() => {
						showSuccessAlert(this.$t('email-sent'));
					})
					.catch(() => {
						showErrorAlert(this.$t('error-sending-email'));
					})
					.finally(() => {
						this.loading = false;
					});
			} else {
				this.loading = false;
			}
		},
		formatCurrency (value) {
			if (value == null || value === '') return ''; // Handle null or empty values gracefully
			return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
		},
		toggleHeaders () {
			const table = this.$refs.table;
			if (table) {
				const keys = Object.keys(table.$vnode.componentInstance.openCache);
				keys.forEach((x) => {
					table.$vnode.componentInstance.openCache[x] = false;
				});
			}
		},
		async getVerifiers () {
			const header = { domainsList: 'INVOICE_STATUS' };
			await this.$store.dispatch('verifiers/getVerifiersForMultipleDomains', header);
		},
		async getInvoices () {
			this.toggleHeaders();
			this.loading = true;
			await this.$store.dispatch('invoices/getInvoices').then(() => {
				this.loading = false;
			});
			this.loading = false;
			this.toggleHeaders();
		},
		addInvoice () {
			this.dialog = true;
			this.dialogType = 'add';
			this.selectedInvoice = null;
		},
		invoiceDetails (item) {
			// call the drawer here  ( commit )
			this.$store.commit('SET_UPDATE_DETAILS_DRAWER', item);
			this.$store.commit('SET_UPDATE_DETAILS_DRAWER', {
				updateDrawer: true,
				page2: 'invoices',
				item: item,
			});
			// this.dialog = true;
			// this.dialogType = 'edit';
			this.selectedInvoice = item;
		},
		closeDialog () {
			this.dialog = false;
			this.dialogType = null;
			this.selectedInvoice = null;
			this.getInvoices();
		},
		invoiceUpdated (res) {
			if (res.resFlag) {
				showSuccessAlert(this.$t('success'));
			} else {
				showErrorAlert(this.$t('failed!'));
			}
			this.dialog = false;
			this.dialogType = null;
			this.selectedInvoice = null;
			this.getInvoices();
		},
		filteredInvoices (group) {
			const term = (this.appBarSearch || '').toLowerCase();
			if (!term) return group.invoices;
			return group.invoices.filter((inv) => {
				const num = String(inv.invoiceNumber).toLowerCase();
				const date = String(inv.invoiceDate).toLowerCase();
				const total = inv.grandTotal.toString();
				const statusItem = this.verifiersInvoiceStatus.find((v) => v.lowValue === inv.status);
				const status = statusItem ? statusItem.shortDescription.toLowerCase() : '';
				return num.includes(term) || date.includes(term) || total.includes(term) || status.includes(term);
			});
		},
		addCustomEmail () {
			if (!this.customEmail.trim()) return;
			this.customEmails.push(this.customEmail);
			this.$set(this.emailSelections, this.customEmail, 'none');
			this.customEmail = '';
		},
		removeCustomEmail (email, index) {
			this.customEmails.splice(index, 1);
			delete this.emailSelections[email];
		},
		async getInvoicePdf () {
			const invoiceData = { invoiceId: this.selectedInvoice.id };
			await this.$store.dispatch('invoices/getInvoicePdf', invoiceData);
			const pdfBlob = new Blob([this.generatedPdfFile.data], { type: 'application/pdf' });
			this.invoicePdfURL = URL.createObjectURL(pdfBlob);
			const contentDisposition = this.generatedPdfFile.headers['content-disposition'];
			const regex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
			const match = contentDisposition ? contentDisposition.match(regex) : null;

			this.pdfFileName = match ? match[1].replace(/['"]/g, '') : `Invoice_${this.selectedInvoice.invoiceNumber}.pdf`;
			this.fileNamePart = this.pdfFileName.replace(/^Invoice_/, '').replace(/\.pdf$/i, '');
		},
		async generateRPMCsv () {
			const selectedYear = this.selectedInvoice.invoiceYear;
			const selectedMonth = this.selectedInvoice.invoiceMonth;
			var clientId = this.selectedInvoice.clientId;
			const organizationData = { organizationId: clientId, month: selectedMonth, year: selectedYear };
			await this.$store.dispatch('billings/generateRPMCsvOrganizationPatientsForMonth', organizationData);
			const buffer = await this.generatedRPMCsvFile.arrayBuffer();
			this.rpmBuffer = read(buffer, { type: 'buffer', cellDates: true, cellNF: false, cellText: true });
			const ws = this.rpmBuffer.Sheets[this.rpmBuffer.SheetNames[0]];
			const rows = utils.sheet_to_json(ws, { raw: true });
			const range = utils.decode_range(ws['!ref']);
			const headers = [];

			for (let C = range.s.c; C <= range.e.c; ++C) {
				const address = utils.encode_cell({ r: 0, c: C });
				const cell = ws[address];
				if (cell && cell.t) {
					headers.push({
						text: cell.w || cell.v,
						value: cell.v,
						cellClass: 'text-no-wrap',
						sortable: true,
					});
				}
			}
			this.tableHeaders = headers;
			this.tableItems = rows;
		},
		async generateRTMCsv () {
			const selectedYear = this.selectedInvoice.invoiceYear;
			const selectedMonth = this.selectedInvoice.invoiceMonth;
			var clientId = this.selectedInvoice.clientId;
			const organizationData = { organizationId: clientId, month: selectedMonth, year: selectedYear };
			await this.$store.dispatch('billings/generateRTMCsvOrganizationPatientsForMonth', organizationData);
			const buffer = await this.generatedRTMCsvFile.arrayBuffer();
			this.rtmBuffer = read(buffer, { type: 'buffer', cellDates: true, cellNF: false, cellText: true });
		},
		async generateCCMCsv () {
			const selectedYear = this.selectedInvoice.invoiceYear;
			const selectedMonth = this.selectedInvoice.invoiceMonth;
			var clientId = this.selectedInvoice.clientId;
			const organizationData = { organizationId: clientId, month: selectedMonth, year: selectedYear };
			await this.$store.dispatch('billings/generateCCMCsvOrganizationPatientsForMonth', organizationData);
			const buffer = await this.generatedCCMCsvFile.arrayBuffer();
			this.ccmBuffer = read(buffer, { type: 'buffer', cellDates: true, cellNF: false, cellText: true });
		},
		generateInvoiceOnlySubject () {
			return `${this.pdfFileName.replace(/\.pdf$/i, '')}`;
		},
		generateInvoiceOnlyBody (grandTotal, invoiceDate, dueDate) {
			return `This is an automated email from Wellness Metric. Please find attached invoice for services provided during ${invoiceDate}.

Invoice summary:
	• Amount due: $${grandTotal}
	• Invoice period: ${invoiceDate}
	• Due date: ${dueDate}

Payment instructions:
	• Can be found in the invoice.
	• Please include the invoice number in the payment reference.

Attachments:
	• Invoice PDF #${this.pdfFileName}

For alternate formats or additional documentation, contact Billing Support: billing-support@wellnessmetric.net or call +1 (715) 214-8041. This email was generated automatically; do not reply to this address.
Automated Billing System — Wellness Metric`;
		},
		generateInvoiceAndBillingSubject () {
			return `${this.pdfFileName.replace(/\.pdf$/i, '')} + Billing Data`;
		},
		generateBillingOnlySubject () {
			return `Billing Data - ${this.fileNamePart}`;
		},
		generateBillingOnlyBody (clientName) {
			const attachmentsList = this.includedFiles
				.map((file) => `	• ${file}_Billing_${this.fileNamePart}.csv\\xlsx`)
				.join('\n');
			return `This is an automated email from Wellness Metric including billing data for forwarding to payers.

Billing data summary:
	• Provider: ${clientName}
	• Reporting month: ${this.fileNamePart}
	• Format provided: CSV / Excel

Attachments:
${attachmentsList}

The attached billing files can be forwarded to each patient’s insurer along with required claim forms. If a payer-specific mapping is required, contact Billing Support at billing-support@wellnessmetric.net and provide the payer name.

This email was generated automatically; do not reply to this address. For assistance, contact Billing Support: billing-support@wellnessmetric.net or +1 (715) 214-8041.

Automated Billing System — Wellness Metric`;
		},
		generateInvoiceAndBillingBody (grandTotal, invoiceDate, dueDate, clientName) {
			const attachmentsList = this.includedFiles
				.map((file) => `	• ${file}_Billing_${this.fileNamePart}.csv\\xlsx`)
				.join('\n');
			return `This is an automated email from Wellness Metric including invoice and billing data for forwarding to payers for services provided during ${invoiceDate}.

Invoice summary:
	• Amount due: ${grandTotal}
	• Due date: ${dueDate}
	• Invoice period: ${invoiceDate}

Billing data summary:
	• Provider: ${clientName}
	• Reporting month: ${invoiceDate}
	• Format provided: CSV / Excel

Attachments:
	• Invoice PDF #${this.pdfFileName}
${attachmentsList}

The attached billing files can be forwarded to each patient’s insurer along with required claim forms. If a payer-specific mapping is required, contact Billing Support at billing-support@wellnessmetric.net and provide the payer name.

This email was generated automatically; do not reply to this address. For assistance, contact Billing Support: billing-support@wellnessmetric.net or +1 (715) 214-8041.

Automated Billing System — Wellness Metric`;
		},
		getEmailTemplate () {
			const date = new Date(this.selectedInvoice.invoiceYear, this.selectedInvoice.invoiceMonth - 1);
			if (this.includeInvoice && this.includedFiles.length === 0) {
				this.emailSubject = this.generateInvoiceOnlySubject();
				this.emailBody = this.generateInvoiceOnlyBody(
					this.selectedInvoice.grandTotal,
					formatterFilter(date, 'date_med_month'),
					formatterFilter(this.selectedInvoice.dueDate, 'date_med'),
				);
			} else if (this.includeInvoice && this.includedFiles.length > 0) {
				this.emailSubject = this.generateInvoiceAndBillingSubject();
				this.emailBody = this.generateInvoiceAndBillingBody(
					this.selectedInvoice.grandTotal,
					formatterFilter(date, 'date_med_month'),
					formatterFilter(this.selectedInvoice.dueDate, 'date_med'),
					this.selectedInvoice.clientName,
				);
			} else if (!this.includeInvoice && this.includedFiles.length > 0) {
				this.emailSubject = this.generateBillingOnlySubject();
				this.emailBody = this.generateBillingOnlyBody(this.selectedInvoice.clientName);
			} else {
				this.emailSubject = '';
				this.emailBody = '';
			}
			this.isCustom = false;
			this.newBodyDialog = false;
		},
	},
	watch: {
		contactEmails: {
			immediate: true,
			handler (newEmails) {
				newEmails.forEach((email) => {
					this.$set(this.emailSelections, email, 'none');
				});
			},
		},
		includedFiles () {
			if (this.emailDialog) this.isCustom ? (this.newBodyDialog = true) : this.getEmailTemplate();
			else this.isCustom = false;
		},
		includeInvoice () {
			if (this.emailDialog) this.isCustom ? (this.newBodyDialog = true) : this.getEmailTemplate();
			else this.isCustom = false;
		},
	},
};
</script>

<style>
.thin-select .v-input__control {
	min-height: 28px !important;
	height: 28px !important;
}
.thin-select .v-input__slot {
	min-height: 28px !important;
	height: 28px !important;
}
.thin-select .v-text-field__slot {
	min-height: 20px !important;
	height: 20px !important;
}
.thin-select .v-select__selection {
	line-height: 20px !important;
	margin-top: 0 !important;
	margin-bottom: 0 !important;
}
.thin-select .v-input__append-inner {
	margin-top: 2px !important;
	margin-bottom: 2px !important;
}
.thin-select .v-select__slot {
	min-height: 20px !important;
}
.thin-select .v-input__prepend-inner {
	margin-top: 6px !important;
}
.thin-text-field .v-input__slot {
	min-height: 36px !important;
}
.full-height-table {
	display: flex;
	flex-direction: column;
	height: 100%;
}
.full-height-table .v-data-table__wrapper {
	flex: 1 1 auto;
}
.no-radio-margin,
.no-radio-margin .v-input--selection-controls__input {
	margin: 0 !important;
}
.full-height-textarea .v-input__control,
.full-height-textarea .v-input__slot,
.full-height-textarea .v-text-field__slot,
.full-height-textarea textarea {
	flex-grow: 1 !important;
	height: 100% !important;
}
</style>
